import { Input, Pagination, Switch } from 'antd';
import { useEffect, useState } from 'react';

import { apiFetch } from '@/api/client';
import { type EmployeeCapability } from '@/api/generated/enums';
import { employeesKey, useEmployees, type Employee } from '@/api/masters';
import { useApiMutation } from '@/api/mutations';
import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { ConfirmButton } from '@/components/ConfirmButton';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { IconAction } from '@/components/IconAction';
import { OneTimePasswordModal, type IssuedPassword } from '@/components/OneTimePasswordModal';
import { Button, Card, EmptyState, PageHeading } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { useDebounced } from '@/components/useDebounced';
import { RecordAction, WriteAction } from '@/components/WriteAction';
import { absent, formatDate } from '@core/format/dates';
import type { Session } from '@core/session/types';

import { capabilityFilters, capabilityLabel } from './employees/capabilities';
import { EmployeeDrawer, type Editing } from './employees/EmployeeDrawer';
import { EmployeeLoginDrawer } from './employees/EmployeeLoginDrawer';
import { linkBlockedReason, unlinkBlockedReason } from './employees/login';

const pageSize = 25;

/**
 * The staff master (spec Section 24).
 *
 * <p>
 * <strong>Beside the customers and the vendors, because all three are party masters.</strong> What
 * this one adds is the half of the shop that is inside it: the karigar in the back room who takes
 * the alteration the workshop down the lane would otherwise get, and the person who fits a wedding
 * party. Both were unnameable until this row existed.
 * </p>
 *
 * <p>
 * <strong>It is not the Users screen and must not be read as one.</strong> Most people here never
 * sign in — a login costs a seat on the plan, so a shop of forty could not enter its staff as users
 * even if the model allowed it. Where somebody does have one, the list says so, and switching an
 * employee off here does not touch their account.
 * </p>
 */
export function EmployeesPage({ session }: { session: Session }) {
  const [search, setSearch] = useState('');
  const [capability, setCapability] = useState<EmployeeCapability | undefined>(undefined);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [givingLoginTo, setGivingLoginTo] = useState<Employee | null>(null);
  const [issued, setIssued] = useState<IssuedPassword | null>(null);

  const debouncedSearch = useDebounced(search);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, capability, includeInactive]);

  const employees = useEmployees(session.branch?.id, {
    search: debouncedSearch,
    capability,
    includeInactive,
    page,
    pageSize,
  });

  /**
   * Detaches the login, leaving the account itself signing in.
   *
   * <strong>That separation is deliberate and is the product rule, not an oversight.</strong>
   * Leaving is an HR event; ending somebody's access is a security event, and it happens on the
   * Users screen. A shop that expected this to lock somebody out would otherwise believe it had.
   */
  const unlink = useApiMutation<Employee, Employee>({
    mutationFn: (employee) =>
      apiFetch<Employee>(`/api/employee/${employee.id}/user`, { method: 'DELETE' }),
    success: (_result, employee) =>
      `${employee.fullName} no longer signs in here. Their account is untouched.`,
    invalidates: [employeesKey],
  });

  const remove = useApiMutation<void, Employee>({
    mutationFn: (employee) => apiFetch<void>(`/api/employee/${employee.id}`, { method: 'DELETE' }),
    success: (_result, employee) => `${employee.fullName} deleted.`,
    invalidates: [employeesKey],
  });

  const columns: DataViewColumn<Employee>[] = [
    {
      title: 'Person',
      dataIndex: 'fullName',
      primary: true,
      render: (_, row) => (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900">{row.fullName}</span>
            {/* Only the exceptions are marked, as on every other master. Branch-local is the
                ordinary case for staff — the reverse of a vendor — so shared is what stands out. */}
            {row.isShared && <StatusBadge tone="brand">All branches</StatusBadge>}
            {!row.isActive && <StatusBadge tone="warning">Left</StatusBadge>}
          </div>
          <span className="font-mono-nums text-[12px] text-default">
            {row.code} · {row.mobile}
            {row.designation ? ` · ${row.designation}` : ''}
          </span>
        </div>
      ),
    },
    {
      title: 'Does',
      key: 'capabilities',
      width: 230,
      render: (_, row) =>
        row.capabilities.length === 0 ? (
          // Not a dash. An empty list is a real and deliberate answer — the accountant — and a dash
          // reads as a record somebody left half filled in.
          <span className="text-[13px] text-default">No rental duties</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {row.capabilities.map((value) => (
              <StatusBadge key={value} tone="neutral">
                {capabilityLabel(value)}
              </StatusBadge>
            ))}
          </div>
        ),
    },
    {
      title: 'Signs in as',
      key: 'username',
      width: 150,
      responsive: ['lg'],
      render: (_, row) =>
        row.username === null ? (
          <span className="text-default">{absent}</span>
        ) : (
          <span className="font-mono-nums text-[13px]">{row.username}</span>
        ),
    },
    {
      title: 'Joined',
      dataIndex: 'dateOfJoining',
      width: 120,
      align: 'right',
      responsive: ['xl'],
      hideOnCard: true,
      render: (_, row) => <span className="font-mono-nums">{formatDate(row.dateOfJoining)}</span>,
    },
    {
      title: '',
      key: 'actions',
      width: 96,
      align: 'right',
      fixed: 'right',
      trailing: true,
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          {/*
            The login, which until now had endpoints and no way to reach them.

            Two grants to give one and one to take it back, so the two are separate controls rather
            than a toggle — a toggle would have to pick one permission and would then either refuse
            a correction or offer an escalation.
          */}
          {row.username === null ? (
            <RecordAction
              session={session}
              required="Employee:Edit"
              forbidden={linkBlockedReason(session)}
            >
              {(disabled) => (
                <IconAction
                  icon="icon icon-key-round"
                  label={`Give ${row.fullName} a login`}
                  disabled={disabled}
                  onClick={() => setGivingLoginTo(row)}
                />
              )}
            </RecordAction>
          ) : (
            <RecordAction
              session={session}
              required="Employee:Edit"
              forbidden={unlinkBlockedReason(session)}
            >
              {(disabled) => (
                <ConfirmButton
                  icon="icon icon-unlink"
                  label={`Detach ${row.username} from ${row.fullName}`}
                  title="Detach this login?"
                  description="The staff record stops naming this account. The account itself keeps working and can still sign in — switching it off is a separate act on the Users screen."
                  confirmText="Detach"
                  disabled={disabled}
                  pending={unlink.isPending}
                  onConfirm={() => void unlink.mutateAsync(row).catch(() => undefined)}
                />
              )}
            </RecordAction>
          )}

          <WriteAction session={session} required="Employee:Edit">
            {(disabled) => (
              <IconAction
                icon="icon icon-pencil-line"
                label={`Edit ${row.fullName}`}
                disabled={disabled}
                onClick={() => setEditing({ mode: 'edit', employee: row })}
              />
            )}
          </WriteAction>

          <RecordAction session={session} required="Employee:Delete">
            {(disabled) => (
              <ConfirmButton
                icon="icon icon-trash-2"
                label={`Delete ${row.fullName}`}
                title="Delete this staff record?"
                description="Only for one entered by mistake. If they have left, edit them and switch off 'Still working here' instead — that keeps the jobs and fittings that name them readable."
                confirmText="Delete"
                disabled={disabled}
                pending={remove.isPending}
                onConfirm={() => void remove.mutateAsync(row).catch(() => undefined)}
              />
            )}
          </RecordAction>
        </div>
      ),
    },
  ];

  if (employees.isError) {
    return <ApiErrorAlert error={employees.error} />;
  }

  const total = employees.data?.total ?? 0;

  return (
    <>
      <PageHeading
        title="Staff"
        subtitle="Who works here, and what you can hand them. Not everybody on this list signs in."
        actions={
          <WriteAction session={session} required="Employee:Create">
            {(disabled) => (
              <Button
                variant="primary"
                icon="icon icon-plus"
                shortcut="create"
                disabled={disabled}
                onClick={() => setEditing({ mode: 'create' })}
              >
                New employee
              </Button>
            )}
          </WriteAction>
        }
      />

      <Card flush className="p-4 pb-0 max-sm:p-3 max-sm:pb-1">
        <div className="mb-4 flex flex-wrap items-center gap-3 max-sm:mb-3 max-sm:gap-2">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name, code or number"
            allowClear
            prefix={<i className="icon icon-search text-default" />}
            className="max-w-72 max-sm:max-w-none max-sm:flex-[1_0_100%]"
          />

          {/* The same question the tailor picker asks the API when it wants the people who can take
              an alteration. Somebody who alters and works the counter appears under both chips. */}
          <div className="flex flex-wrap gap-1">
            {capabilityFilters.map((filter) => (
              <button
                key={filter.label}
                type="button"
                onClick={() => setCapability(filter.value)}
                aria-pressed={capability === filter.value}
                className={`rounded border px-2.5 py-1 text-[13px] ${
                  capability === filter.value
                    ? 'border-primary bg-primary text-white'
                    : 'border-border-color text-default hover:text-dark'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          <label className="inline-flex cursor-pointer items-center gap-2 text-[13px] text-default sm:ms-auto">
            <Switch size="small" checked={includeInactive} onChange={setIncludeInactive} />
            Show people who have left
          </label>
        </div>

        <DataView<Employee>
          columns={columns}
          dataSource={employees.data?.items ?? []}
          loading={employees.isLoading}
          empty={
            <EmptyState
              icon="icon icon-id-card"
              title={debouncedSearch || capability ? 'Nobody matches that' : 'No staff yet'}
              hint={
                debouncedSearch || capability
                  ? 'Try part of the number instead — the search matches it with or without spaces.'
                  : 'Add the people who work here. A tailor listed here can take an alteration without it going outside, and a docket will print in their own language.'
              }
            />
          }
        />

        {total > pageSize && (
          <div className="flex justify-end border-t border-border-color py-3">
            <Pagination
              current={page}
              total={total}
              pageSize={pageSize}
              onChange={setPage}
              showSizeChanger={false}
              showTotal={(count) => `${count} people`}
            />
          </div>
        )}
      </Card>

      {editing && <EmployeeDrawer editing={editing} onClose={() => setEditing(null)} />}

      <EmployeeLoginDrawer
        employee={givingLoginTo}
        onClose={() => setGivingLoginTo(null)}
        onIssued={setIssued}
      />

      {/*
        Outside the drawer, so it survives the drawer closing. The password is shown once and exists
        nowhere else — unmounting it with its parent would lose the one value that cannot be fetched
        again, at the exact moment it was issued.
      */}
      <OneTimePasswordModal issued={issued} onClose={() => setIssued(null)} />
    </>
  );
}
